import { Component } from '@angular/core';
import { CustomerInterface } from '@contracts/interfaces/customer.interface';
import { InvoiceInterface } from '@contracts/interfaces/invoice.interface';
import { InvoicesService } from 'src/app/services/invoices.service';
import { Task } from '@contracts/interfaces/task.interface';
import { TaskService } from 'src/app/services/task.service';
import { CustomerService } from 'src/app/services/customer.service';
import { CustomerDialogService } from 'src/app/dialogs/customer/customer.service.dialog';
import { Transaction } from '@contracts/interfaces/transaction.interfaces';
import { TransactionsService } from 'src/app/services/transactions.service';
import { ACCOUNT } from 'src/app/accounts';

@Component({
  selector: 'app-search-page',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
})
export class SearchPageComponent {
  private readonly expenseAccounts = [ACCOUNT.EXPENSES, ACCOUNT.G72EXPENSES];

  revenueQuery = '';
  isSearchingRevenue = false;
  revenueSearchError = false;
  revenueTransactions: Transaction[] = [];
  private revenueSearchToken = 0;

  invoiceQuery = '';
  lastInvoiceQuery = '';
  isSearchingInvoices = false;
  invalidInvoiceQuery = false;
  invoices: InvoiceInterface[] = [];
  invoiceAmountQuery = '';
  lastInvoiceAmountQuery = '';
  isSearchingInvoicesByAmount = false;
  invalidInvoiceAmountQuery = false;
  invoicesByAmount: InvoiceInterface[] = [];
  invoiceLineItemQuery = '';
  lastInvoiceLineItemQuery = '';
  isSearchingInvoicesByLineItem = false;
  invoicesByLineItem: InvoiceInterface[] = [];
  taskQuery = '';
  lastTaskQuery = '';
  isSearchingTasks = false;
  invalidTaskQuery = false;
  tasks: Task[] = [];
  customerQuery = '';
  lastCustomerQuery = '';
  isSearchingCustomers = false;
  customers: CustomerInterface[] = [];
  customerPhoneQuery = '';
  lastCustomerPhoneQuery = '';
  isSearchingCustomersByPhone = false;
  invalidCustomerPhoneQuery = false;
  customersByPhone: CustomerInterface[] = [];
  transactionAmountQuery = '';
  lastTransactionAmountQuery = '';
  transactionYearQuery = '';
  lastTransactionYearQuery = '';
  isSearchingTransactionsByAmount = false;
  invalidTransactionAmountQuery = false;
  invalidTransactionYearQuery = false;
  transactionsByAmount: Transaction[] = [];

  constructor(
    private invoicesService: InvoicesService,
    private taskService: TaskService,
    private customerService: CustomerService,
    private customerDialogService: CustomerDialogService,
    private transactionsService: TransactionsService
  ) {}

  onInvoiceQueryChange(query: string): void {
    this.invoiceQuery = query;
    this.lastInvoiceQuery = query;
    this.invalidInvoiceQuery = false;

    const invoiceNumber = parseInt((query || '').trim(), 10);
    if (isNaN(invoiceNumber)) {
      this.isSearchingInvoices = false;
      this.invalidInvoiceQuery = true;
      this.invoices = [];
      return;
    }

    this.isSearchingInvoices = true;
    this.invoicesService.get(invoiceNumber).subscribe({
      next: (invoice) => {
        this.invoices = invoice ? [invoice] : [];
        this.isSearchingInvoices = false;
      },
      error: () => {
        this.invoices = [];
        this.isSearchingInvoices = false;
      }
    });
  }

  onInvoiceCancelled(): void {
    this.invoiceQuery = '';
    this.lastInvoiceQuery = '';
    this.isSearchingInvoices = false;
    this.invalidInvoiceQuery = false;
    this.invoices = [];
  }

  onInvoiceAmountQueryChange(query: string): void {
    this.invoiceAmountQuery = query;
    this.lastInvoiceAmountQuery = query;
    this.invalidInvoiceAmountQuery = false;

    const normalised = (query || '').trim().replace(/[$,\s]/g, '');
    if (!normalised) {
      this.isSearchingInvoicesByAmount = false;
      this.invoicesByAmount = [];
      return;
    }

    const amount = parseFloat(normalised);
    if (isNaN(amount)) {
      this.isSearchingInvoicesByAmount = false;
      this.invalidInvoiceAmountQuery = true;
      this.invoicesByAmount = [];
      return;
    }

    const amountInCents = Math.round(amount * 100);
    this.isSearchingInvoicesByAmount = true;

    this.invoicesService.find({ total: amountInCents }).subscribe({
      next: (invoices: InvoiceInterface[]) => {
        this.invoicesByAmount = invoices || [];
        this.isSearchingInvoicesByAmount = false;
      },
      error: () => {
        this.invoicesByAmount = [];
        this.isSearchingInvoicesByAmount = false;
      }
    });
  }

  onInvoiceAmountCancelled(): void {
    this.invoiceAmountQuery = '';
    this.lastInvoiceAmountQuery = '';
    this.isSearchingInvoicesByAmount = false;
    this.invalidInvoiceAmountQuery = false;
    this.invoicesByAmount = [];
  }

  onInvoiceLineItemQueryChange(query: string): void {
    this.invoiceLineItemQuery = query;
    this.lastInvoiceLineItemQuery = query;

    const normalised = (query || '').trim();
    if (!normalised) {
      this.isSearchingInvoicesByLineItem = false;
      this.invoicesByLineItem = [];
      return;
    }

    this.isSearchingInvoicesByLineItem = true;

    this.invoicesService.find({
      'lineItems.desc': {
        $regex: this.escapeRegex(normalised),
        $options: 'i'
      }
    }).subscribe({
      next: (invoices: InvoiceInterface[]) => {
        this.invoicesByLineItem = invoices || [];
        this.isSearchingInvoicesByLineItem = false;
      },
      error: () => {
        this.invoicesByLineItem = [];
        this.isSearchingInvoicesByLineItem = false;
      }
    });
  }

  onInvoiceLineItemCancelled(): void {
    this.invoiceLineItemQuery = '';
    this.lastInvoiceLineItemQuery = '';
    this.isSearchingInvoicesByLineItem = false;
    this.invoicesByLineItem = [];
  }

  openInvoice(invoice: InvoiceInterface): void {
    this.invoicesService.openInvoiceDialog(invoice, () => {
      if (this.lastInvoiceQuery) {
        this.onInvoiceQueryChange(this.lastInvoiceQuery);
      }
      if (this.lastInvoiceAmountQuery) {
        this.onInvoiceAmountQueryChange(this.lastInvoiceAmountQuery);
      }
      if (this.lastInvoiceLineItemQuery) {
        this.onInvoiceLineItemQueryChange(this.lastInvoiceLineItemQuery);
      }
    });
  }

  onTaskQueryChange(query: string): void {
    this.taskQuery = query;
    this.lastTaskQuery = query;
    this.invalidTaskQuery = false;

    const taskId = parseInt((query || '').trim(), 10);
    if (isNaN(taskId)) {
      this.isSearchingTasks = false;
      this.invalidTaskQuery = true;
      this.tasks = [];
      return;
    }

    this.isSearchingTasks = true;
    this.taskService.get(taskId).subscribe({
      next: (task: Task | null) => {
        this.tasks = task ? [task] : [];
        this.isSearchingTasks = false;
      },
      error: () => {
        this.tasks = [];
        this.isSearchingTasks = false;
      }
    });
  }

  onTaskCancelled(): void {
    this.taskQuery = '';
    this.lastTaskQuery = '';
    this.isSearchingTasks = false;
    this.invalidTaskQuery = false;
    this.tasks = [];
  }

  openTask(task: Task): void {
    this.taskService.openTaskDialog(task, [task.id], 0, () => {
      if (this.lastTaskQuery) {
        this.onTaskQueryChange(this.lastTaskQuery);
      }
    });
  }

  onCustomerQueryChange(query: string): void {
    this.customerQuery = query;
    this.lastCustomerQuery = query;

    const normalised = (query || '').trim();
    if (!normalised) {
      this.isSearchingCustomers = false;
      this.customers = [];
      return;
    }

    this.isSearchingCustomers = true;
    this.customerService.search(normalised, 10).subscribe({
      next: (customers: CustomerInterface[]) => {
        this.customers = customers || [];
        this.isSearchingCustomers = false;
      },
      error: () => {
        this.customers = [];
        this.isSearchingCustomers = false;
      }
    });
  }

  onCustomerPhoneQueryChange(query: string): void {
    this.customerPhoneQuery = query;
    this.lastCustomerPhoneQuery = query;
    this.invalidCustomerPhoneQuery = false;

    const digits = (query || '').replace(/\D/g, '');
    if (!digits) {
      this.isSearchingCustomersByPhone = false;
      this.invalidCustomerPhoneQuery = !!(query || '').trim();
      this.customersByPhone = [];
      return;
    }

    const pattern = digits.split('').join('\\D*');
    this.isSearchingCustomersByPhone = true;

    this.customerService.find({
      phone: {
        $regex: pattern,
        $options: 'i'
      }
    }, 0, 10).subscribe({
      next: (customers: CustomerInterface[]) => {
        this.customersByPhone = customers || [];
        this.isSearchingCustomersByPhone = false;
      },
      error: () => {
        this.customersByPhone = [];
        this.isSearchingCustomersByPhone = false;
      }
    });
  }

  onCustomerCancelled(): void {
    this.customerQuery = '';
    this.lastCustomerQuery = '';
    this.isSearchingCustomers = false;
    this.customers = [];
  }

  onCustomerPhoneCancelled(): void {
    this.customerPhoneQuery = '';
    this.lastCustomerPhoneQuery = '';
    this.isSearchingCustomersByPhone = false;
    this.invalidCustomerPhoneQuery = false;
    this.customersByPhone = [];
  }

  openCustomer(customer: CustomerInterface): void {
    this.customerDialogService.open(customer, '', () => {
      if (this.lastCustomerQuery) {
        this.onCustomerQueryChange(this.lastCustomerQuery);
      }
      if (this.lastCustomerPhoneQuery) {
        this.onCustomerPhoneQueryChange(this.lastCustomerPhoneQuery);
      }
    });
  }

  onRevenueQueryChange(query: string): void {
    this.revenueQuery = query;
    const token = ++this.revenueSearchToken;
    const text = (query || '').trim();
    this.revenueSearchError = false;
    this.revenueTransactions = [];
    this.isSearchingRevenue = !!text;
    if (!text) return;

    const pattern = { $regex: this.escapeRegex(text), $options: 'i' };
    const matches: any[] = [{ description: pattern }, { note: pattern }];
    if (/^\d+$/.test(text)) {
      matches.push({ 'invoice.number': { $in: [Number(text), text] } });
    }
    this.transactionsService.find({
      'source.account': ACCOUNT.REVENUE,
      $or: matches
    }, 0, 100, { date: -1 }).subscribe({
      next: (transactions: Transaction[]) => {
        if (token !== this.revenueSearchToken) return;
        this.revenueTransactions = transactions || [];
        this.isSearchingRevenue = false;
      },
      error: () => {
        if (token !== this.revenueSearchToken) return;
        this.revenueSearchError = true;
        this.isSearchingRevenue = false;
      }
    });
  }

  onRevenueCancelled(): void {
    this.onRevenueQueryChange('');
  }

  openRevenueTransaction(transaction: Transaction): void {
    this.transactionsService.openTransactionDialog(null, transaction.source.sha1, 0, this.revenueTransactions, () => {
      if (this.revenueQuery.trim()) this.onRevenueQueryChange(this.revenueQuery);
    });
  }

  onTransactionAmountQueryChange(query: string): void {
    this.transactionAmountQuery = query;
    this.lastTransactionAmountQuery = query;
    this.invalidTransactionAmountQuery = false;
    this.searchTransactionsByAmount();
  }

  onTransactionYearQueryChange(query: string): void {
    this.transactionYearQuery = query;
    this.lastTransactionYearQuery = query;
    this.invalidTransactionYearQuery = false;
    this.searchTransactionsByAmount();
  }

  private searchTransactionsByAmount(): void {
    this.invalidTransactionAmountQuery = false;
    this.invalidTransactionYearQuery = false;

    const normalised = (this.transactionAmountQuery || '').trim().replace(/[$,\s]/g, '');
    if (!normalised) {
      this.isSearchingTransactionsByAmount = false;
      this.transactionsByAmount = [];
      return;
    }

    const amount = parseFloat(normalised);
    if (isNaN(amount)) {
      this.isSearchingTransactionsByAmount = false;
      this.invalidTransactionAmountQuery = true;
      this.transactionsByAmount = [];
      return;
    }

    const amountInCents = Math.round(amount * 100);

    const yearRange = this.getTransactionYearRange();
    if (yearRange === false) {
      this.isSearchingTransactionsByAmount = false;
      this.invalidTransactionYearQuery = true;
      this.transactionsByAmount = [];
      return;
    }

    const query: any = {
      'source.account': { $in: this.expenseAccounts },
      amount: { $in: [amountInCents, -amountInCents] }
    };

    if (yearRange) {
      query.date = {
        $gte: yearRange.start,
        $lte: yearRange.end
      };
    }

    this.isSearchingTransactionsByAmount = true;

    this.transactionsService.find(query, 0, 20, { date: -1 }).subscribe({
      next: (transactions: Transaction[]) => {
        this.transactionsByAmount = transactions || [];
        this.isSearchingTransactionsByAmount = false;
      },
      error: () => {
        this.transactionsByAmount = [];
        this.isSearchingTransactionsByAmount = false;
      }
    });
  }

  onTransactionAmountCancelled(): void {
    this.transactionAmountQuery = '';
    this.lastTransactionAmountQuery = '';
    this.transactionYearQuery = '';
    this.lastTransactionYearQuery = '';
    this.isSearchingTransactionsByAmount = false;
    this.invalidTransactionAmountQuery = false;
    this.invalidTransactionYearQuery = false;
    this.transactionsByAmount = [];
  }

  openTransaction(transaction: Transaction): void {
    this.transactionsService.openTransactionDialog(null, transaction.source.sha1, 0, this.transactionsByAmount, () => {
      if (this.lastTransactionAmountQuery) {
        this.searchTransactionsByAmount();
      }
    });
  }

  private getTransactionYearRange(): { start: Date; end: Date } | null | false {
    const yearText = (this.transactionYearQuery || '').trim();
    if (!yearText) {
      return null;
    }

    const year = parseInt(yearText, 10);
    if (!/^\d{4}$/.test(yearText) || isNaN(year)) {
      return false;
    }

    return {
      start: new Date(year, 0, 1, 0, 0, 0, 0),
      end: new Date(year, 11, 31, 23, 59, 59, 999)
    };
  }

  private escapeRegex(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  get showInvoiceEmptyState(): boolean {
    return !this.lastInvoiceQuery && !this.lastInvoiceAmountQuery && !this.lastInvoiceLineItemQuery;
  }

  get showInvoiceNoResultsYet(): boolean {
    return this.hasInvoiceSearch &&
      !this.isSearchingAnyInvoice &&
      !this.invalidInvoiceQuery &&
      !this.invalidInvoiceAmountQuery &&
      this.combinedInvoices.length === 0;
  }

  get showInvoiceResults(): boolean {
    return this.combinedInvoices.length > 0;
  }

  get hasInvoiceSearch(): boolean {
    return !!this.lastInvoiceQuery || !!this.lastInvoiceAmountQuery || !!this.lastInvoiceLineItemQuery;
  }

  get isSearchingAnyInvoice(): boolean {
    return this.isSearchingInvoices || this.isSearchingInvoicesByAmount || this.isSearchingInvoicesByLineItem;
  }

  get combinedInvoices(): InvoiceInterface[] {
    const combined = [...this.invoices, ...this.invoicesByAmount, ...this.invoicesByLineItem];
    const seen = new Set<number>();

    return combined.filter((invoice) => {
      if (!invoice?.number || seen.has(invoice.number)) {
        return false;
      }
      seen.add(invoice.number);
      return true;
    });
  }

  get showTaskEmptyState(): boolean {
    return !this.lastTaskQuery;
  }

  get showTaskNoResultsYet(): boolean {
    return !!this.lastTaskQuery && !this.isSearchingTasks && !this.invalidTaskQuery && this.tasks.length === 0;
  }

  get showTaskResults(): boolean {
    return this.tasks.length > 0;
  }

  get showCustomerEmptyState(): boolean {
    return !this.lastCustomerQuery && !this.lastCustomerPhoneQuery;
  }

  get showCustomerNoResultsYet(): boolean {
    return this.hasCustomerSearch &&
      !this.isSearchingAnyCustomer &&
      !this.invalidCustomerPhoneQuery &&
      this.combinedCustomers.length === 0;
  }

  get showCustomerResults(): boolean {
    return this.combinedCustomers.length > 0;
  }

  get hasCustomerSearch(): boolean {
    return !!this.lastCustomerQuery || !!this.lastCustomerPhoneQuery;
  }

  get isSearchingAnyCustomer(): boolean {
    return this.isSearchingCustomers || this.isSearchingCustomersByPhone;
  }

  get combinedCustomers(): CustomerInterface[] {
    const combined = [...this.customers, ...this.customersByPhone];
    const seen = new Set<string>();

    return combined.filter((customer) => {
      const key = customer?._id || `${customer?.name || ''}|${customer?.phone || ''}|${customer?.email || ''}`;
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  get showTransactionEmptyState(): boolean {
    return !this.lastTransactionAmountQuery;
  }

  get showTransactionNoResultsYet(): boolean {
    return !!this.lastTransactionAmountQuery &&
      !this.isSearchingTransactionsByAmount &&
      !this.invalidTransactionAmountQuery &&
      this.transactionsByAmount.length === 0;
  }

  get showTransactionResults(): boolean {
    return this.transactionsByAmount.length > 0;
  }
}
